import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  leaveTypeFormSchema,
  type CreateLeaveTypePayload,
  type LeaveType,
  type LeaveTypeFormValues,
  type UpdateLeaveTypePayload,
} from '../types/leaveType.types';
import { useCreateLeaveType } from './useCreateLeaveType';
import { useUpdateLeaveType } from './useUpdateLeaveType';

interface UseLeaveTypeFormParams {
  leaveType: LeaveType | null;
  open: boolean;
  onClose: () => void;
}

const EMPTY_DEFAULTS: LeaveTypeFormValues = {
  code: '',
  name: '',
  description: '',
  isActive: true,
};

function toFormValues(leaveType: LeaveType | null): LeaveTypeFormValues {
  if (!leaveType) return EMPTY_DEFAULTS;
  return {
    code: leaveType.code,
    name: leaveType.name,
    description: leaveType.description ?? '',
    isActive: leaveType.isActive,
  };
}

function buildCreatePayload(values: LeaveTypeFormValues): CreateLeaveTypePayload {
  const trimmedDescription = values.description.trim();
  const payload: CreateLeaveTypePayload = {
    code: values.code,
    name: values.name,
    isActive: values.isActive,
  };
  if (trimmedDescription.length > 0) {
    payload.description = trimmedDescription;
  }
  return payload;
}

export function useLeaveTypeForm({ leaveType, open, onClose }: UseLeaveTypeFormParams) {
  const isEditMode = leaveType !== null;
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const form = useForm<LeaveTypeFormValues>({
    resolver: zodResolver(leaveTypeFormSchema),
    defaultValues: toFormValues(leaveType),
    mode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      form.reset(toFormValues(leaveType));
    } else {
      form.reset(EMPTY_DEFAULTS);
    }
  }, [leaveType, open, form]);

  const createMutation = useCreateLeaveType(onClose);
  const updateMutation = useUpdateLeaveType(onClose);

  const isPending = isEditMode ? updateMutation.isPending : createMutation.isPending;

  function submit(values: LeaveTypeFormValues) {
    if (isEditMode && leaveType) {
      const changedKeys = Object.keys(form.formState.dirtyFields) as (keyof LeaveTypeFormValues)[];
      if (changedKeys.length === 0) {
        onClose();
        return;
      }
      const updatePayload: UpdateLeaveTypePayload = Object.fromEntries(
        changedKeys.map((key) => [key, values[key]]),
      );
      updateMutation.mutate({ id: leaveType.id, payload: updatePayload });
      return;
    }
    createMutation.mutate(buildCreatePayload(values));
  }

  function requestClose() {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }
    setIsDiscardDialogOpen(true);
  }

  function confirmDiscard() {
    setIsDiscardDialogOpen(false);
    onClose();
  }

  function cancelDiscard() {
    setIsDiscardDialogOpen(false);
  }

  return {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  };
}
